import * as React from "react";
import classNames from "classnames";

import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import CloseIcon from "@material-ui/icons/Close";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import { makeStyles, Theme, withStyles } from "@material-ui/core/styles";
import Red from "@material-ui/core/colors/red";
import DialogActions from "@material-ui/core/DialogActions";
import Alert from "@material-ui/lab/Alert";
import Checkbox, { CheckboxProps } from "@material-ui/core/Checkbox";
import { useCancelProduct } from "./hooks/product";
import { format } from "lib/globalize-util";
import CircularProgress from "material-ui/CircularProgress";
import theme from "styles/theme";

interface IProps {
    open: boolean;
    productId: string;
    onClose: () => void;
    onConfirm: () => void;
    onEntriesRefresh: () => void;
}

const NuCheckbox = withStyles({
    root: {
        color: theme.palette.darkGrey,
        "&$checked": {
            color: theme.palette.darkPrimary1,
        },
    },
    checked: {},
})((props: CheckboxProps) => <Checkbox color="default" {...props} />);

const CancelProductConfirmation: React.FC<IProps> = ({ open, productId, onConfirm, onClose, onEntriesRefresh }) => {
    const [isArchiveChecked, setIsArchiveChecked] = React.useState(false);
    const [isRemovedChecked, setIsRemovedChecked] = React.useState(false);

    const titleClasses = useTitleStyles({});
    const classes = useStyles();

    const [shouldCancel, setShouldCancel] = React.useState(false);
    const { data, isLoading } = useCancelProduct(productId, { enabled: shouldCancel });

    React.useEffect(() => {
        if (data) {
            onEntriesRefresh();
            setShouldCancel(false);
            onClose();
            onConfirm();
        }
    }, [data]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleCancelProduct = React.useCallback(() => {
        setShouldCancel(true);
    }, []);

    const handleCheckBoxChange = (e) => {
        const checkBoxes = {
            removed: (state) => setIsRemovedChecked(state),
            archived: (state) => setIsArchiveChecked(state),
        };
        checkBoxes[e.target.name](e.target.checked);
    };

    return (
        <Dialog open={open} onClose={onClose} classes={{ root: classes.confirmationDialog }}>
            <DialogTitle className={titleClasses.dialogTitle} disableTypography={true}>
                <InfoOutlinedIcon fontSize="large" className={classes.infoIcon} />
                <span className={classes.titleText}>{format("Cancel Product")}</span>
                <IconButton className={titleClasses.close} aria-label="close" onClick={onClose}>
                    <CloseIcon className={titleClasses.iconClose} />
                </IconButton>
            </DialogTitle>
            {isLoading ? (
                <CircularProgress style={{ display: "block", margin: "10px auto" }} />
            ) : (
                <DialogContent className={classes.content}>
                    <Alert icon={false} severity="error" classes={{ root: classes.rootAlert }}>
                        {format("Warning: This action cannot be undone.")}
                    </Alert>

                    <div className={classes.textContent}>
                        {format(
                            "Cancelling a product will move the product to archive and also add the product to Cancelled Styles. Once cancelled, the product will be removed from any order that is subsequently edited. Please acknowledge both of the checkboxes below to verify this."
                        )}
                    </div>

                    <FormControlLabel
                        classes={{ label: classes.checkboxLabel }}
                        control={
                            <NuCheckbox checked={isArchiveChecked} onChange={handleCheckBoxChange} name="archived" />
                        }
                        label={format("Product will be archived and marked as Cancelled")}
                    />
                    <FormControlLabel
                        classes={{ label: classes.checkboxLabel }}
                        control={
                            <NuCheckbox checked={isRemovedChecked} onChange={handleCheckBoxChange} name="removed" />
                        }
                        label={format("Product will be removed from associated orders")}
                    />
                </DialogContent>
            )}

            <DialogActions className={classes.actions} disableSpacing={true}>
                <Button classes={{ root: classes.button }} variant="outlined" onClick={onClose}>
                    {format("Back")}
                </Button>
                <Button
                    classes={{
                        root: classNames(classes.button, classes.confirm),
                        disabled: classes.disabledConfirmBtn,
                    }}
                    disabled={!(isArchiveChecked && isRemovedChecked) || isLoading}
                    variant="contained"
                    onClick={handleCancelProduct}
                    disableElevation={true}
                >
                    {format("Cancel Product")}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

const useTitleStyles = makeStyles({
    dialogTitle: {
        fontSize: 16,
        fontWeight: "bold",
        borderBottom: `1px solid ${theme.palette.lightGrey}`,
    },
    close: {
        position: "absolute",
        top: 5,
        right: 7,
    },
    iconClose: {
        width: 24,
        height: 24,
        color: theme.palette.darkestGrey,
    },
});

const useStyles = makeStyles<Theme>({
    button: {
        height: 40,
        borderRadius: 20,
        fontSize: 13,
        fontFamily: "inherit",
        textTransform: "initial",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
    },
    confirm: {
        color: "white",
        marginLeft: 20,
        backgroundColor: theme.palette.errorColor,
        "&:hover": {
            backgroundColor: Red[600],
        },
    },
    disabledConfirmBtn: {
        backgroundColor: `${theme.palette.errorColor} !important`,
        color: "white !important",
        opacity: 0.5,
    },
    content: {
        padding: "24px 24px 40px",
        fontSize: "14px",
    },
    actions: {
        padding: 24,
    },
    infoIcon: {
        color: theme.palette.errorColor,
        verticalAlign: "middle",
    },
    titleText: {
        paddingLeft: "10px",
        verticalAlign: "middle",
    },
    confirmationDialog: {
        zIndex: "1600 !important" as any,
    },
    checkboxLabel: {
        fontSize: 14,
        fontFamily: "inherit",
    },
    rootAlert: {
        fontSize: 14,
        margin: 5,
        fontFamily: "inherit",
        color: theme.palette.warning,
        border: "1px solid",
    },
    textContent: {
        marginTop: 20,
        marginBottom: 20,
        fontSize: 12,
        color: theme.palette.darkGrey,
    },
});

export default CancelProductConfirmation;
